import { useCallback, useEffect, useRef } from "react";

/**
 * Wraps an async callback so that rapid, repeated invocations collapse into
 * a single call (trailing-edge debounce) carrying the latest arguments, and
 * so a new invocation never overlaps one still in flight -- if the debounce
 * timer fires while a previous call hasn't resolved yet, the latest args are
 * queued and fired the moment it settles, rather than starting a second,
 * concurrent request.
 *
 * Generic on purpose (not tied to any particular API call) so it can be
 * reused anywhere a burst of UI actions should only ever produce one
 * in-flight request at a time.
 */
export function useDebouncedAsyncCallback<Args extends unknown[]>(
  callback: (...args: Args) => Promise<void>,
  delayMs: number
): (...args: Args) => void {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const inFlightRef = useRef(false);
  const pendingArgsRef = useRef<Args | null>(null);

  const runNow = useCallback((): void => {
    const args = pendingArgsRef.current;
    if (!args) return;

    if (inFlightRef.current) {
      // Something is already in flight; leave these as the latest pending
      // args so they run as soon as it finishes (never run concurrently).
      return;
    }

    pendingArgsRef.current = null;
    inFlightRef.current = true;
    // Routed through Promise.resolve().then(...) so a callback that throws
    // synchronously behaves the same as one that returns a rejected promise
    // -- otherwise a synchronous throw would skip `.finally` entirely and
    // leave `inFlightRef` stuck `true` forever. The `.catch` (which callers
    // should not rely on for their own error handling) exists only so this
    // internal bookkeeping chain never surfaces as an unhandled rejection.
    void Promise.resolve()
      .then(async () => {
        await callbackRef.current(...args);
      })
      .catch(() => {})
      .finally(() => {
        inFlightRef.current = false;
        if (pendingArgsRef.current) {
          runNow();
        }
      });
  }, []);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    []
  );

  return useCallback(
    (...args: Args): void => {
      pendingArgsRef.current = args;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(runNow, delayMs);
    },
    [delayMs, runNow]
  );
}
